// Nightly predator resolution. Veteran-only (REFOCUS-2026-09.md §3).
//
// Kept out of simulate.ts because this resolves once a night rather than every
// tick, and because the yard-defence summary it needs is a different shape from
// anything the per-bird loop uses.
//
// Until this existed, `nightsWithoutPredator` was initialised to 0 and never
// written, `PREDATOR_ATTACK` was declared and never emitted, and the
// `predator_proof` badge could not be earned — which in turn made the Chicken
// Keeper Certificate unreachable. Locking the coop at night, one of the five
// daily chores, had no consequence attached to it at all.

import type { Rng } from '../core/rng';
import type { GameState } from '../core/types';
import type { BiologyConfig } from './config';
import { addCondition } from './disease';
import { effectsOf } from './items';
import { rollStream } from './league';
import {
  rollPredators,
  type Predator,
  type PredatorActivity,
  type PredatorPressure,
  type RegionId,
  type YardDefense,
} from './predators';
import { dayOf, hourOfDay } from './time';
import { averageLitterDirtiness, summarizeFacilities } from './welfare';

/** Predators resolve at these hours, once each per sim day. */
const NIGHT_HOUR = 22;
const DAY_HOUR = 13;

/** Litter this soiled means feed and droppings underfoot, which draws vermin. */
const SPILLED_FEED_DIRTINESS = 30;

export interface PredatorOutcome {
  predator: Predator;
  when: PredatorActivity;
  /** It got in. A false here is a near miss, which is still worth telling. */
  breached: boolean;
  /** Birds badly frightened by it. */
  scared: string[];
  eggsLost: number;
  /** Veteran only — normal mode never kills (GAME-DESIGN.md §4.5). */
  deaths: string[];
}

/** State the predator system owns. Optional so older saves load without it. */
export interface PredatorState {
  pressure: PredatorPressure;
  /** Sim-day of the last resolved night, so a night resolves exactly once. */
  lastNightDay: number;
  lastDayDay: number;
}

function predatorState(state: GameState): PredatorState {
  if (!state.predators) {
    state.predators = { pressure: {}, lastNightDay: -1, lastDayDay: -1 };
  }
  return state.predators;
}

/**
 * How well the yard is built and how loudly it advertises itself. The coop
 * ladder is gone (REFOCUS §7), so every defense is something the yard actually
 * has: a lockable coop with its doors shut, hardware cloth on the run, a roof
 * or shade to get under, netting overhead. `fenceInches` stays 0: the field
 * survives in `YardDefense` because the breach math for other regions still
 * reads it, but nothing sets it.
 */
export function yardDefense(state: GameState): YardDefense {
  const facilities = summarizeFacilities(state);
  const lockable = state.structures.some((s) => effectsOf(s).lockable);
  const roofed = state.structures.some((s) => effectsOf(s).roof);
  const eggsInNest = facilities.nestBoxes.reduce(
    (sum, box) => sum + (box.state.eggs?.length ?? 0),
    0,
  );
  const alive = state.flock.filter((c) => c.alive);

  return {
    // A coop with no door to shut is not a locked coop.
    coopLocked: lockable && facilities.coops.length > 0 && !facilities.anyDoorOpen,
    hardwareCloth: facilities.hasHardwareCloth,
    fenceInches: 0,
    overheadCover: roofed || facilities.hasShade,
    overheadNet: facilities.hasOverheadNet,
    spilledFeed: averageLitterDirtiness(state) >= SPILLED_FEED_DIRTINESS,
    birds: alive.filter((c) => c.stage !== 'chick').length,
    chicks: alive.filter((c) => c.stage === 'chick').length,
    eggsInNest,
  };
}

/**
 * The roll for one predator window, on its own stream.
 *
 * Predators used to be handed the step's shared `sim:<hour>` stream, which made
 * them the first system in the game to depend on how many times an unrelated
 * one happened to draw that hour. The behaviour machine in particular draws
 * three times per bird on a normal step and not at all on a step where a hot
 * bird short-circuits to shade (PLAN-04 S3), so adding that branch silently
 * re-rolled the predator for every night in a hot season. `streamFor` promises
 * a named stream is "independent of call order elsewhere"; a night raid is not
 * a consequence of how thirsty the hens were, so it gets its own name.
 *
 * Keyed by sim-day and window, so a night is one roll a night in Normal mode
 * too, and the same night is the same roll for every player in a league.
 */
function predatorStream(state: GameState, when: PredatorActivity): Rng {
  return rollStream(state, `predator:${dayOf(state.simHour)}:${when}`);
}

/**
 * Resolves any predator window the clock has just crossed. Returns what
 * happened, or null on a quiet night — which is the common case in a well-kept
 * yard, and is what earns the badge.
 */
export function stepPredatorNight(
  state: GameState,
  config: BiologyConfig,
): PredatorOutcome | null {
  const memory = predatorState(state);
  const today = dayOf(state.simHour);
  const hour = hourOfDay(state.simHour);

  let when: PredatorActivity | null = null;
  if (hour >= NIGHT_HOUR && memory.lastNightDay < today) {
    memory.lastNightDay = today;
    when = 'night';
  } else if (hour >= DAY_HOUR && hour < NIGHT_HOUR && memory.lastDayDay < today) {
    memory.lastDayDay = today;
    when = 'day';
  }
  if (!when) return null;

  // Predators are veteran-only (RELEASE-PLAN.md §6). The hour-window
  // bookkeeping above still runs in every mode so `nightsWithoutPredator`
  // keeps incrementing on clean nights in normal play too — otherwise the
  // `predator_proof` badge (14 clean nights) would be unreachable outside
  // veteran, and the mode-agnostic certificate would break.
  if (!state.veteran.enabled) {
    if (when === 'night') state.progression.nightsWithoutPredator++;
    return null;
  }

  const region: RegionId = state.region ?? 'pr';
  const rng = predatorStream(state, when);
  const attempt = rollPredators(region, when, yardDefense(state), memory.pressure, rng);

  if (!attempt || !attempt.breached) {
    // A near miss still counts as a clean night — the yard held, which is
    // precisely what the player is being asked to achieve.
    if (when === 'night') state.progression.nightsWithoutPredator++;
    return attempt ? { ...attempt, when, scared: [], eggsLost: 0, deaths: [] } : null;
  }

  state.progression.nightsWithoutPredator = 0;
  return applyBreach(state, config, attempt.predator, when, rng);
}

function applyBreach(
  state: GameState,
  config: BiologyConfig,
  predator: Predator,
  when: PredatorActivity,
  rng: Rng,
): PredatorOutcome {
  const outcome: PredatorOutcome = {
    predator,
    when,
    breached: true,
    scared: [],
    eggsLost: 0,
    deaths: [],
  };

  if (predator.targets === 'eggs') {
    for (const box of state.structures) {
      const eggs = box.state.eggs;
      if (!eggs?.length) continue;
      outcome.eggsLost += eggs.length;
      box.state.eggs = [];
    }
  }

  // Everything in the yard knows something came through, whatever it came for.
  const alive = state.flock.filter((c) => c.alive);
  for (const chicken of alive) {
    if (addCondition(chicken, 'scared', state.simHour, 'moderate')) {
      outcome.scared.push(chicken.id);
    }
  }

  // Normal mode never kills. A breach is a mess to wake up to and a fright for
  // the flock, not a body — enough to change behaviour, safe for the audience.
  if (!config.grace.sickNeverDie && predator.targets !== 'eggs' && alive.length > 0) {
    const taken = alive[rng.int(0, alive.length - 1)];
    taken.alive = false;
    taken.deathCause = 'predator';
    taken.diedOnDay = dayOf(state.simHour);
    outcome.deaths.push(taken.id);
  }

  return outcome;
}
